import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { IconLock } from '@/components/Icons';
import { useT } from '@/i18n';
import { formatMoney } from '@/lib/money';
import { formatDateTime } from '@/lib/time';
import { formatNYClock, todayNY } from '@/market/marketHours';
import { computeTotals } from '@/market/value';
import { estimateCost, formatUsd } from '@/reports/cost';
import { usePriceFeed } from '@/reports/priceFeed';
import { generateReport } from '@/reports/generate';
import { TEMPLATES, templateName, type ReportTemplate } from '@/reports/templates';
import { activeLLM, hasLLM, useSettings } from '@/settings/settingsStore';
import { useAppState } from '@/state/appStore';
import { heldOnly, type Report, type ReportTemplateId } from '@/types';
import { CopyPromptDialog } from './CopyPromptDialog';
import s from './reports.module.css';

export function ReportsView() {
  const { t } = useT();
  const app = useAppState();
  const settings = useSettings();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState('');
  const [copyFor, setCopyFor] = useState<ReportTemplateId | null>(null);

  const llm = activeLLM(settings);
  const llmReady = hasLLM(settings);
  const provider = settings.llm.activeProvider;
  const feed = usePriceFeed(); // re-renders the estimates when the live prices land
  const now = Date.now();
  const today = todayNY(now);
  const held = useMemo(() => heldOnly(app.assets), [app.assets]); // watch items are not reported on
  const totals = useMemo(() => computeTotals(held, app.quotes, app.fx), [held, app.quotes, app.fx]);
  const reports = useMemo(() => Object.values(app.reports).filter((r): r is Report => !!r).sort((a, b) => b.generatedAt - a.generatedAt), [app.reports]);

  const run = (id: ReportTemplateId) => {
    generateReport(id, { scenario }).then((r) => navigate(`/reports/${r.id}`)).catch(() => undefined);
  };

  const card = (tpl: ReportTemplate) => {
    const searches = tpl.estimateSearches(held);
    const est = estimateCost(provider, llm?.model ?? '', searches, feed);
    const latest = reports.find((r) => r.templateId === tpl.id);
    const available = tpl.available(now);
    const busy = app.reportBusy === tpl.id;
    const canRun = llmReady && held.length > 0 && available && !app.reportBusy;
    return (
      <article key={tpl.id} className={s.tpl}>
        <div className={s.tplHead}>
          <span className={`serif ${s.tplName}`}>{templateName(tpl.id)}</span>
          <span className="muted" style={{ fontSize: 12 }}>{t(`tpl.${tpl.id}.when` as const)}</span>
        </div>
        <p className={s.tplDesc}>{t(`tpl.${tpl.id}.desc` as const)}</p>
        {tpl.id === 'stress' && <input className={`input ${s.scenario}`} value={scenario} placeholder={t('reports.scenarioPlaceholder')} onChange={(e) => setScenario(e.target.value)} />}
        <div className={s.meta}>
          {tpl.needsWeb ? <span className="badge"><i className="dot delay" />{t('reports.needsWeb')}</span> : <span className="badge"><i className="dot" />{t('reports.offline')}</span>}
          {tpl.needsWeb && <span className="badge num">{t('reports.searches', { n: searches })}</span>}
          {llmReady && est.usd != null && <span className="badge num">{t('reports.estCost', { usd: formatUsd(est.usd) })}</span>}
        </div>
        {app.reportErrors[tpl.id] && <p className={`down ${s.tplError}`}>{app.reportErrors[tpl.id]}</p>}
        <div className={s.tplFoot}>
          {!available ? (
            <span className="badge"><i className="dot close" />{t('reports.waitClose')}</span>
          ) : latest ? (
            <span className="muted num" style={{ fontSize: 12 }}>
              {latest.date === today ? t('reports.lastToday', { time: formatNYClock(latest.generatedAt) }) : t('reports.last', { date: formatDateTime(latest.generatedAt) })} · <Link to={`/reports/${latest.id}`}>{t('reports.view')}</Link>
            </span>
          ) : (
            <span className="muted" style={{ fontSize: 12 }}>{t('reports.never')}</span>
          )}
          <div className={s.btns}>
            <button type="button" className="btn quiet" disabled={held.length === 0} onClick={() => setCopyFor(tpl.id)}>{t('reports.copy')}</button>
            <button type="button" className="btn primary" disabled={!canRun} title={!llmReady ? t('reports.noKeyBadge') : held.length === 0 ? t('reports.needAssets') : undefined} onClick={() => run(tpl.id)}>{busy ? t('reports.running') : t('reports.run')}</button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="page">
      <Header crumbs={[{ label: t('header.list'), to: '/' }]} title={t('reports.title')} showCurrency={false} actions={
        llmReady ? <span className="badge"><i className="dot" />{t('reports.llmBadge', { model: llm?.model ?? '' })}</span> : <Link to="/settings" className="btn quiet">{t('reports.noKeyBadge')}</Link>
      } />

      <section className={s.intro}>
        <div>
          <h2 className={`serif ${s.h2}`}>{t('reports.heading')}</h2>
          <p className={`muted ${s.lead}`}>{t('reports.lead')}</p>
        </div>
        {held.length > 0 && (
          <span className="muted num" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{t('reports.snapshot', { time: formatNYClock(app.lastRefreshAt ?? now), n: held.length, total: formatMoney(totals.totalUsd, 'USD') })}</span>
        )}
      </section>

      <section className={s.body}>
        <div className={s.grid}>{TEMPLATES.map(card)}</div>

        <aside className={s.aside}>
          <div>
            <div className={`label ${s.asideTitle}`}>{t('reports.waysTitle')}</div>
            <div className={s.way}>
              <span className={s.wayN}>1</span>
              <div className={s.wayBody}><span style={{ fontWeight: 500 }}>{t('reports.way1Title')}</span><p>{t('reports.way1Body')}</p></div>
            </div>
            <div className={s.way}>
              <span className={s.wayN}>2</span>
              <div className={s.wayBody}><span style={{ fontWeight: 500 }}>{t('reports.way2Title')}</span><p>{t('reports.way2Body')}</p></div>
            </div>
          </div>

          <div className={s.privacy}>
            <IconLock size={16} style={{ color: 'var(--link)', flex: 'none', marginTop: 2 }} />
            <span>{t('reports.privacy')}</span>
          </div>

          <div>
            <div className={`label ${s.asideTitle}`}>{t('reports.recent')}</div>
            {reports.length === 0 && <p className="muted" style={{ fontSize: 13 }}>{t('reports.noRecent')}</p>}
            {reports.slice(0, 8).map((r) => (
              <div key={r.id} className={s.hist}>
                <span><Link to={`/reports/${r.id}`}>{templateName(r.templateId)}</Link> <span className="muted">· {formatDateTime(r.generatedAt)}</span></span>
                <span className="muted num">{r.model}{r.costUsd != null ? ` · ${formatUsd(r.costUsd)}` : ''}</span>
              </div>
            ))}
          </div>
        </aside>
      </section>

      {copyFor && <CopyPromptDialog templateId={copyFor} scenario={scenario} onClose={() => setCopyFor(null)} onGenerate={llmReady ? () => { const id = copyFor; setCopyFor(null); run(id); } : undefined} />}
    </div>
  );
}
